import { supabase } from "@/integrations/supabase/client";
import {
  buildStayDayDates,
  reconcileStayDays,
  type ExistingStayDay,
  type StayDayRecurrenceInput,
  type StayDayStatus,
} from "@/lib/daycare-hotel/stayDays";

export interface MaterializeStayDaysArgs {
  reservationId: string;
  clinicId: string;
  petId: string;
  moduleType: "daycare" | "hotel" | "both";
  checkInDate: string;
  checkOutDate: string | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  period: string | null;
  hotelRoomId: string | null;
  hotelRoomBedId: string | null;
  recurrence: StayDayRecurrenceInput | null;
  pickedDates?: string[];
  excludedDates?: string[];
}

/**
 * Grava os dias que a reserva ocupa. Chamado na criação e na edição: dias
 * `scheduled` são regerados a partir da regra vigente, dias já realizados ou
 * decididos à mão são preservados (ver `reconcileStayDays`).
 *
 * `stay_reservation_days` não existe em `types.ts` (arquivo gerado, intocável),
 * daí o cast — mesmo padrão de `useCreateStayReservation`.
 */
export async function materializeStayDays(args: MaterializeStayDaysArgs): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = supabase as any;

  const { data, error } = await client
    .from("stay_reservation_days")
    .select("id, stay_date, status")
    .eq("stay_reservation_id", args.reservationId);
  if (error) throw error;

  const existing: ExistingStayDay[] = ((data ?? []) as Array<{ id: string; stay_date: string; status: string }>).map(
    (row) => ({ id: row.id, stayDate: row.stay_date, status: row.status as StayDayStatus }),
  );

  const desiredDates = buildStayDayDates({
    moduleType: args.moduleType,
    checkInDate: args.checkInDate,
    checkOutDate: args.checkOutDate,
    recurrence: args.recurrence,
    pickedDates: args.pickedDates,
    excludedDates: args.excludedDates,
  });

  const { datesToInsert, dayIdsToDelete } = reconcileStayDays({ existing, desiredDates });

  if (datesToInsert.length > 0) {
    const { error: insertError } = await client.from("stay_reservation_days").insert(
      datesToInsert.map((stayDate) => ({
        clinic_id: args.clinicId,
        stay_reservation_id: args.reservationId,
        pet_id: args.petId,
        stay_date: stayDate,
        status: "scheduled",
        check_in_time: args.checkInTime,
        check_out_time: args.checkOutTime,
        period: args.period,
        hotel_room_id: args.hotelRoomId,
        hotel_room_bed_id: args.hotelRoomBedId,
      })),
    );
    if (insertError) throw insertError;
  }

  if (dayIdsToDelete.length > 0) {
    const { error: deleteError } = await client.from("stay_reservation_days").delete().in("id", dayIdsToDelete);
    if (deleteError) throw deleteError;
  }
}
